import { useMemo } from 'react';
import { useViewedTenantAccount } from './useViewedTenantAccount';
import { useGetTaxCategoriesQuery } from '../api/taxCategory/taxCategoryApiSlice';
import { useGetProductsQuery } from '../api/group/groupAdminApiSlice';
import type { PriceEntryMode } from '../api/account/accountApiTypes';
import { PriceTaxRate, priceListTaxCategoryIds, rateOf } from '../utils/priceTaxRate';

/**
 * How this tenant's prices are typed (#2967, docs/plans/net-price-entry.md).
 * A Kleinunternehmer charges no VAT, so net and gross are the same and entry
 * is always gross. The viewed tenant, not the caller's: a master editing
 * another tenant's prices follows that tenant's setting.
 */
export const usePriceEntryMode = (): PriceEntryMode => {
    const { accountTenant } = useViewedTenantAccount();
    if (!accountTenant || accountTenant.isKleinunternehmer) return 'gross';
    return accountTenant.priceEntry === 'net' ? 'net' : 'gross';
};

export const useTaxRateOf = (taxCategoryIds: ReadonlyArray<string | null | undefined>): PriceTaxRate => {
    const { data } = useGetTaxCategoriesQuery();
    // A string key keeps the memo stable across renders that pass a fresh array.
    const key = taxCategoryIds.map((id) => id ?? '').join('|');
    return useMemo(() => rateOf(key === '' ? [] : key.split('|'), data?.categories ?? []), [key, data]);
};

/**
 * The VAT rate of one price list: the categories of every product priced from
 * it must agree (`priceListTaxCategoryIds`, `rateOf`). The one source for every
 * price field and price table, so an editor and the table beside it can never
 * convert at different rates.
 */
export const usePriceListTaxRate = (priceCategoryId: string | null | undefined): PriceTaxRate => {
    const { data: products } = useGetProductsQuery(undefined, { skip: !priceCategoryId });
    const ids = priceCategoryId
        ? priceListTaxCategoryIds(priceCategoryId, (products ?? []).map((product) => ({
            priceCategoryId: product.priceCategory?.id ?? null,
            taxCategoryId: product.taxCategoryId,
        })))
        : [];
    return useTaxRateOf(ids);
};
